import { toast } from "@/components/ui";
import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import type { ApiError } from "@/hooks/useApi";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import { QuestionnaireCategory } from "@/network/useQuestionnaires";
import {
  QUESTIONNAIRE_NAMES,
  type QuestionnaireResponseBase,
} from "@/types/questionnaires";
import type { Procedure } from "@curai/js-fhir-tools";
import { useQueryClient } from "@tanstack/react-query";

interface GetMiscellaneousNotesResponse {
  notes: Procedure[];
}

export const useGetMiscellaneousNotes = (patientId: string | undefined) => {
  return useApi<GetMiscellaneousNotesResponse>({
    queryOptions: {
      enabled: Boolean(patientId),
      queryKey:
        QueryKeys.Patient.MedicalRecords.MiscellaneousNotes.DEFAULT(patientId),
      queryFn: async () => {
        const response = await axiosInstance.get(
          "/charting/procedures/misc-note-search",
          {
            params: { patientId },
          },
        );
        return response?.data;
      },
    },
  });
};

interface AddMiscellaneousNoteRequest {
  patientId: string;
  note: string;
}

export const useAddMiscellaneousNote = ({
  patientId,
  onSuccess,
  onError,
}: {
  patientId: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();
  return useApiMutation({
    mutationOptions: {
      mutationFn: (data: AddMiscellaneousNoteRequest) =>
        axiosInstance.post("/charting/procedures/misc-note", data),
      mutationKey:
        QueryKeys.Patient.MedicalRecords.MiscellaneousNotes.CREATE(patientId),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.MiscellaneousNotes.DEFAULT(
              patientId,
            ),
        });
        onSuccess?.();
      },
      onError,
    },
  });
};

interface UpdateMiscellaneousNoteRequest {
  note: string;
}

export const useUpdateMiscellaneousNote = ({
  procedureId,
  patientId,
  onSuccess,
}: {
  procedureId: string;
  patientId: string;
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();
  return useApiMutation({
    mutationOptions: {
      mutationFn: (data: UpdateMiscellaneousNoteRequest) =>
        axiosInstance.patch(
          `/charting/procedures/misc-note/${procedureId}`,
          data,
        ),
      mutationKey: QueryKeys.Patient.MedicalRecords.MiscellaneousNotes.UPDATE(
        patientId,
        procedureId,
      ),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.MiscellaneousNotes.DEFAULT(
              patientId,
            ),
        });
        onSuccess?.();
      },
    },
  });
};

export const useResolveMiscellaneousNote = ({
  procedureId,
  patientId,
  onSuccess,
}: {
  procedureId: string;
  patientId: string;
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();
  return useApiMutation({
    mutationOptions: {
      mutationFn: () =>
        axiosInstance.patch(
          `/charting/procedures/misc-note/${procedureId}/resolve`,
        ),
      mutationKey: QueryKeys.Patient.MedicalRecords.MiscellaneousNotes.RESOLVE(
        patientId,
        procedureId,
      ),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.MiscellaneousNotes.DEFAULT(
              patientId,
            ),
        });
        onSuccess?.();
      },
    },
  });
};

export const useVoidMiscellaneousNote = ({
  procedureId,
  patientId,
  onSuccess,
}: {
  procedureId: string;
  patientId: string;
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();
  return useApiMutation<void, void>({
    mutationOptions: {
      mutationFn: () =>
        axiosInstance.delete(
          `/charting/procedures/misc-note/${procedureId}/void`,
        ),
      mutationKey: QueryKeys.Patient.MedicalRecords.MiscellaneousNotes.VOID(
        patientId,
        procedureId,
      ),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.MiscellaneousNotes.DEFAULT(
              patientId,
            ),
        });
        onSuccess?.();
      },
    },
  });
};

export interface SendQuestionnaireRequest {
  questionnaireId: string;
  questionnaireName: string;
  messageText?: string;
  patientId: string;
}

export const useSendQuestionnaire = ({
  patientId,
  onSuccess,
  onError,
}: {
  patientId: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();

  return useApiMutation({
    mutationOptions: {
      mutationFn: async (data: SendQuestionnaireRequest) => {
        return axiosInstance.post(
          "/charting/questionnaire/send-questionnaire",
          {
            questionnaireId: data.questionnaireId,
            questionnaireName: data.questionnaireName,
            patientId: data.patientId,
          },
        );
      },
      mutationKey:
        QueryKeys.Patient.MedicalRecords.Questionnaires.SEND(patientId),
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Questionnaires.DEFAULT(
            patientId,
            QuestionnaireCategory.LIFESTYLE,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Questionnaires.DEFAULT(
            patientId,
            QuestionnaireCategory.MENTAL_HEALTH,
          ),
        });
        // Invalidate available actions query for the specific questionnaire type
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.Questionnaires.AVAILABLE_ACTIONS(
              patientId,
              variables.questionnaireId,
            ),
        });
        onSuccess?.();
      },
      onError,
    },
  });
};

export const useConfirmQuestionnaire = ({
  questionnaireResponseId,
  patientId,
  questionnairesCategory,
  questionnaireType,
  onSuccess,
  onError,
}: {
  questionnaireResponseId: string;
  patientId: string;
  questionnairesCategory: "lifestyle" | "mental-health";
  questionnaireType: string;
  onSuccess?: () => void;
  onError?: (error: ApiError) => void;
}) => {
  const queryClient = useQueryClient();

  return useApiMutation({
    mutationOptions: {
      mutationFn: () =>
        axiosInstance.patch(
          `/charting/questionnaire/${questionnaireResponseId}/confirm`,
        ),
      mutationKey: QueryKeys.Patient.MedicalRecords.Questionnaires.CONFIRM(
        patientId,
        questionnaireResponseId,
      ),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Questionnaires.DEFAULT(
            patientId,
            questionnairesCategory,
          ),
        });
        // Invalidate history query so the history modal shows updated confirmation status
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Questionnaires.HISTORY(
            patientId,
            questionnaireType,
          ),
        });
        // Invalidate available actions query so the send button becomes enabled after review
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.Questionnaires.AVAILABLE_ACTIONS(
              patientId,
              questionnaireType,
            ),
        });
        // Invalidate the specific questionnaire response query so the detail view updates
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Questionnaires.BY_ID(
            patientId,
            questionnaireResponseId,
          ),
        });
        const questionnaireInfo =
          QUESTIONNAIRE_NAMES[
            questionnaireType as keyof typeof QUESTIONNAIRE_NAMES
          ];
        const displayName = questionnaireInfo
          ? `${questionnaireInfo.displayName} (${questionnaireInfo.shorthandName})`
          : questionnaireType;
        toast.success(`Reviewed ${displayName} questionnaire`);
        onSuccess?.();
      },
      onError,
    },
  });
};

interface VoidQuestionnaireVariables {
  questionnaireResponseId: string;
}

export const useVoidQuestionnaire = ({
  patientId,
  questionnaireType,
  onSuccess,
}: {
  patientId: string;
  questionnaireType: string;
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();

  return useApiMutation<void, VoidQuestionnaireVariables>({
    mutationOptions: {
      mutationFn: ({ questionnaireResponseId }) =>
        axiosInstance.patch(
          `/charting/questionnaire/${questionnaireResponseId}/void`,
        ),
      mutationKey:
        QueryKeys.Patient.MedicalRecords.Questionnaires.VOID(patientId),
      onSuccess: (_, variables) => {
        // Invalidate questionnaire lists so the voided response disappears
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Questionnaires.DEFAULT(
            patientId,
            QuestionnaireCategory.LIFESTYLE,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Questionnaires.DEFAULT(
            patientId,
            QuestionnaireCategory.MENTAL_HEALTH,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Questionnaires.HISTORY(
            patientId,
            questionnaireType,
          ),
        });
        // Invalidate available actions query in case voiding affects send availability
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.Questionnaires.AVAILABLE_ACTIONS(
              patientId,
              questionnaireType,
            ),
        });
        // Invalidate the specific questionnaire response query so the detail view updates
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Questionnaires.BY_ID(
            patientId,
            variables.questionnaireResponseId,
          ),
        });
        onSuccess?.();
      },
    },
  });
};

export enum QuestionnaireActionType {
  SEND_QUESTIONNAIRE = "send-questionnaire",
  VOID_QUESTIONNAIRE = "void-questionnaire",
}

export enum QuestionnaireUnavailableReason {
  UNIVERSAL_CLINIC_PATIENT = "UNIVERSAL_CLINIC_PATIENT",
  NOT_LICENSED_CLINICIAN = "NOT_LICENSED_CLINICIAN",
  PENDING_REVIEW = "PENDING_REVIEW",
}

export const QUESTIONNAIRE_REASON_LABELS: Record<
  QuestionnaireUnavailableReason,
  string
> = {
  [QuestionnaireUnavailableReason.UNIVERSAL_CLINIC_PATIENT]:
    "Questionnaires cannot be sent to Universal Clinic patients",
  [QuestionnaireUnavailableReason.NOT_LICENSED_CLINICIAN]:
    "Only licensed clinicians or Medical Assistants can send questionnaires",
  [QuestionnaireUnavailableReason.PENDING_REVIEW]:
    "Cannot send questionnaire while a completed questionnaire is pending review",
};

export interface QuestionnaireAvailableAction {
  action: QuestionnaireActionType;
  unavailableReason?: QuestionnaireUnavailableReason;
}

interface QuestionnaireAvailableActionsResponse {
  availableActions?: QuestionnaireAvailableAction[];
}

export const useQuestionnaireAvailableActions = (
  patientId?: string,
  questionnaireType?: string,
) => {
  return useApi<
    QuestionnaireAvailableActionsResponse,
    Record<QuestionnaireActionType, QuestionnaireAvailableAction>
  >({
    queryOptions: {
      enabled: Boolean(patientId),
      queryKey:
        QueryKeys.Patient.MedicalRecords.Questionnaires.AVAILABLE_ACTIONS(
          patientId,
          questionnaireType,
        ),
      queryFn: async () => {
        const response =
          await axiosInstance.get<QuestionnaireAvailableActionsResponse>(
            "/charting/questionnaire/available-actions",
            {
              params: { patientId, questionnaireType },
            },
          );
        return response?.data;
      },
      select: (data) => {
        const actions = data.availableActions || [];

        const result = actions.reduce(
          (acc, action) => {
            acc[action.action] = action;
            return acc;
          },
          {} as Record<QuestionnaireActionType, QuestionnaireAvailableAction>,
        );
        return result;
      },
    },
  });
};

interface GetQuestionnaireByIdResponse {
  questionnaireResponse: QuestionnaireResponseBase;
}

export const useGetQuestionnaireById = (
  questionnaireResponseId: string | undefined,
  patientId: string,
  enabled = true,
) => {
  return useApi<GetQuestionnaireByIdResponse, QuestionnaireResponseBase>({
    queryOptions: {
      enabled: Boolean(questionnaireResponseId && enabled),
      queryKey: QueryKeys.Patient.MedicalRecords.Questionnaires.BY_ID(
        patientId,
        questionnaireResponseId || "",
      ),
      queryFn: async () => {
        const response = await axiosInstance.get<GetQuestionnaireByIdResponse>(
          `/charting/questionnaire/${questionnaireResponseId}`,
        );
        return response?.data;
      },
      select: (data) => data.questionnaireResponse,
    },
  });
};
